/**
 * D2 — Admin → Voice Trunks tab.
 *
 * CRUD UI for voice_trunks rows (Phase 1 backend). Supports BYO + managed
 * Twilio creation, encrypted-field masking on display, test-trunk wet-run,
 * soft-delete with audit preservation.
 */
import React, { useEffect, useState } from "react";
import { COLORS } from "../../theme/colors";
import { Button, Badge, Card, Input, Modal } from "../../components/ui";
import { outboundVoiceApi } from "../../api/client";

// D10 — Strict E.164 client-side validator. Backend rejects with HTTP 422 if
// violated but a client-side guard saves a round-trip. Spec: "+" + country
// code 1-9 + 7-14 more digits = 8-15 total digit characters after the "+".
const E164_RE = /^\+[1-9]\d{7,14}$/;

// Exported for unit testing
export function isValidE164Trunk(v) {
  return !!v && E164_RE.test(v);
}

// Exported for unit testing — shape the create-trunk request. For managed
// Twilio the BYO + LiveKit fields the operator never filled are stripped so the
// backend validator sees a clean shape (an empty livekit_trunk_id triggers the
// auto-provision branch — see voice_trunks.py::create_trunk). BYO payloads pass
// through untouched.
export function buildTrunkPayload(data) {
  const payload = { ...data };
  if (payload.carrier_kind === "twilio") {
    delete payload.sip_uri;
    delete payload.auth_password;
    // Leave auth_username out too — the platform will generate it.
    delete payload.auth_username;
    if (!payload.livekit_trunk_id) {
      delete payload.livekit_trunk_id;
    }
  }
  return payload;
}

export default function VoiceTrunksTab() {
  const [trunks, setTrunks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showTest, setShowTest] = useState(null);  // trunk row or null
  const [confirmDelete, setConfirmDelete] = useState(null);  // D9: trunk row or null
  const [deleting, setDeleting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await outboundVoiceApi.listTrunks();
      setTrunks(res?.data || res || []);
    } catch (e) { console.error("trunks load failed", e); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  // D9 — replaces native confirm() with a styled ConfirmDialog so the
  // operator sees clear copy explaining the soft-delete semantics +
  // 7-year audit retention + how to fully purge if needed. The
  // browser confirm() was easy to misread (one-line text, OS dialog
  // styling differs from app chrome, no way to mention cascade rules).
  const handleDelete = (trunk) => setConfirmDelete(trunk);

  const performDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await outboundVoiceApi.deleteTrunk(confirmDelete.id);
      setConfirmDelete(null);
      await refresh();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Voice trunks</h3>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {/* G-LINK — deep link back to the unified Voice Console
              so the operator can see drift / status / audit
              alongside trunk-level edits. */}
          <a
            href="#admin-voice-console"
            onClick={(e) => {
              e.preventDefault();
              window.dispatchEvent(new CustomEvent("admin:goto-tab", { detail: { id: "voice_console" } }));
            }}
            style={{ fontSize: 12, color: COLORS.accent || "#5960b2", textDecoration: "none" }}
          >
            ← Voice Console
          </a>
          <Button onClick={() => setShowCreate(true)}>+ New trunk</Button>
        </div>
      </div>

      {loading ? (
        <div style={{ color: COLORS.textMuted }}>Loading trunks…</div>
      ) : trunks.length === 0 ? (
        <Card padding="24px">
          <div style={{ textAlign: "center", color: COLORS.textMuted }}>
            <div style={{ fontSize: 14, marginBottom: 8 }}>No voice trunks configured yet.</div>
            <div style={{ fontSize: 13 }}>
              Add a BYO SIP trunk or a managed Twilio sub-account to start dispatching outbound calls.
            </div>
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {trunks.map(t => (
            <TrunkRow key={t.id} trunk={t} onTest={() => setShowTest(t)} onDelete={() => handleDelete(t)} />
          ))}
        </div>
      )}

      <CreateTrunkModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={refresh} />
      <TestTrunkModal trunk={showTest} onClose={() => setShowTest(null)} />
      <DeleteTrunkConfirm trunk={confirmDelete} busy={deleting}
        onClose={() => setConfirmDelete(null)} onConfirm={performDelete} />
    </div>
  );
}


// D9 — Styled ConfirmDialog for trunk soft-delete. Replaces the
// browser's confirm() so the operator gets:
//   - clear copy on what soft-delete means (row marked deleted_at,
//     audit_logs retained 7 years per regulatory requirement)
//   - the trunk's name + caller-id visible without scrolling
//   - a clearly-destructive red Delete button (not the default OS
//     button styling which can mask intent)
//   - "Cancel" as the primary path (Esc + click-outside both
//     close without firing delete)
function DeleteTrunkConfirm({ trunk, busy, onClose, onConfirm }) {
  if (!trunk) return null;
  return (
    <Modal open={!!trunk} onClose={busy ? () => {} : onClose}
      title="Delete voice trunk?" width={520}
      footer={
        <>
          <Button onClick={onClose} variant="ghost" disabled={busy}>Cancel</Button>
          <Button onClick={onConfirm} disabled={busy}
            style={{ background: COLORS.pink || "#ef4444", borderColor: COLORS.pink || "#ef4444", color: "#fff" }}>
            {busy ? "Deleting…" : "Delete trunk"}
          </Button>
        </>
      }>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, fontSize: 13 }}>
        <div>
          You are about to soft-delete <strong>{trunk.name}</strong>
          {trunk.caller_id_e164 && <> (caller ID <code>{trunk.caller_id_e164}</code>)</>}.
        </div>
        <div style={{ background: `${COLORS.orange}10`, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "10px 12px", color: COLORS.text }}>
          <strong>What this does:</strong>
          <ul style={{ margin: "6px 0 0 16px", padding: 0 }}>
            <li>The trunk row is marked <code>deleted_at</code> and immediately removed from the dispatcher's selection pool.</li>
            <li>In-flight calls drain cleanly; no new dispatch picks this trunk.</li>
            <li>Any campaign currently selecting this trunk pauses with <code>paused_reason=trunk_deleted</code>.</li>
            <li><code>voice_call_audit</code> + <code>voice_call_dispatch_log</code> rows are retained 7 years per regulatory requirement (no cascade).</li>
          </ul>
        </div>
        <div style={{ color: COLORS.textMuted, fontSize: 12 }}>
          To fully reclaim the underlying provider resources (Twilio elastic-trunk SID, LiveKit registration, etc.) re-run with <code>?cascade=true</code> via the API — this UI does soft-delete only.
        </div>
      </div>
    </Modal>
  );
}


function TrunkRow({ trunk, onTest, onDelete }) {
  const statusColor =
    trunk.status === "active" ? COLORS.green :
    trunk.status === "degraded" ? COLORS.orange :
    COLORS.textMuted;
  // STIR/SHAKEN attestation tier colour — A=green (full attestation,
  // best chance of caller-ID display), B=orange (partial), C=muted
  // (gateway, often spam-flagged). NULL = unknown (BYO or not yet
  // queried).
  const tierColor =
    trunk.attestation_tier === "A" ? COLORS.green :
    trunk.attestation_tier === "B" ? COLORS.orange :
    trunk.attestation_tier === "C" ? COLORS.textMuted :
    null;
  return (
    <Card padding="14px 18px" data-testid={`voice-trunk-row-${trunk.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <strong>{trunk.name}</strong>
            <Badge color={statusColor}>{trunk.status}</Badge>
            <Badge color={COLORS.textMuted}>{trunk.carrier_kind}</Badge>
            {trunk.auto_provisioned && (
              <Badge color={COLORS.accent} data-testid="trunk-auto-pill">auto</Badge>
            )}
            {tierColor && (
              <Badge color={tierColor} data-testid={`trunk-tier-${trunk.attestation_tier}`}>
                Tier {trunk.attestation_tier}
              </Badge>
            )}
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>
            Caller-ID: {trunk.caller_id_e164} · Cap: ${trunk.daily_spend_cap_usd}/day · Rate: ${trunk.rate_per_minute_usd}/min · Max {trunk.max_concurrent_calls} concurrent
          </div>
          {trunk.circuit_breaker_state === "open" && (
            <div style={{ fontSize: 12, color: COLORS.pink, marginTop: 4 }}>
              ⚠ Circuit breaker OPEN — dispatcher refusing new calls on this trunk
            </div>
          )}
        </div>
        <Button onClick={onTest} variant="ghost">Test</Button>
        <Button onClick={onDelete} variant="ghost">Delete</Button>
      </div>
    </Card>
  );
}


function CreateTrunkModal({ open, onClose, onCreated }) {
  // Default to "twilio" because the auto-provision path is the
  // recommended one (canonical design §4.3). Operators with a BYO
  // trunk can flip the selector.
  const [data, setData] = useState({
    name: "",
    carrier_kind: "twilio",
    livekit_trunk_id: "",
    sip_uri: "",
    auth_username: "",
    auth_password: "",
    caller_id_e164: "",
    region_iso: "US",
    max_concurrent_calls: 5,
    max_cps: 1.0,
    daily_spend_cap_usd: 10.0,
    rate_per_minute_usd: 0.01,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      // For managed-Twilio auto-provision, strip out the BYO + LK
      // fields the operator didn't fill so the backend's validator
      // sees a clean shape. (Empty livekit_trunk_id triggers the
      // auto-provision branch — see voice_trunks.py::create_trunk.)
      const payload = buildTrunkPayload(data);
      await outboundVoiceApi.createTrunk(payload);
      onCreated();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  const isManaged = data.carrier_kind === "twilio";

  return (
    <Modal
      open={open} onClose={onClose} title="New voice trunk" width={560}
      footer={<>
        <Button onClick={onClose} variant="ghost">Cancel</Button>
        <Button onClick={submit} disabled={saving} data-testid="create-trunk-submit">
          {saving ? "Creating…" : "Create trunk"}
        </Button>
      </>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="create-trunk-modal-body">
        <Input label="Name" value={data.name} onChange={e => setData({ ...data, name: e.target.value })} helper="Human-readable; tenant-unique" />
        <div>
          <div style={{ fontSize: 13, marginBottom: 4 }}>Carrier</div>
          <select
            value={data.carrier_kind}
            onChange={e => setData({ ...data, carrier_kind: e.target.value })}
            data-testid="trunk-carrier-select"
            style={{ width: "100%", padding: 8, background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, borderRadius: 6 }}
          >
            <option value="twilio">Managed Twilio (recommended)</option>
            <option value="byo">BYO SIP trunk</option>
          </select>
        </div>

        {isManaged ? (
          // §4.3: tenant never sees LiveKit-specific config; SIP creds
          // are platform-generated. Display the helper text so the
          // operator understands what's happening behind the scenes
          // and which credentials the platform pulls from settings.
          <div
            data-testid="managed-trunk-helper"
            style={{
              padding: "10px 12px",
              background: `${COLORS.accent}12`,
              border: `1px solid ${COLORS.accent}40`,
              borderRadius: 6,
              fontSize: 12,
              color: COLORS.text,
              lineHeight: 1.5,
            }}
          >
            We'll provision your Twilio Elastic SIP Trunk + LiveKit outbound
            trunk over TLS using the Twilio credentials configured in
            Admin → Integrations → Twilio. No LiveKit trunk ID needed.
          </div>
        ) : (
          // BYO: the platform auto-provisions the LK trunk from the
          // tenant's SIP URI + credentials (I11). LK trunk ID is NOT
          // requested from the operator — same §4.2 conformance as
          // managed Twilio.
          <>
            <div
              data-testid="byo-trunk-helper"
              style={{
                padding: "10px 12px",
                background: `${COLORS.accent}12`,
                border: `1px solid ${COLORS.accent}40`,
                borderRadius: 6,
                fontSize: 12,
                color: COLORS.text,
                lineHeight: 1.5,
              }}
            >
              We register your trunk on LiveKit automatically using the SIP
              credentials below. No LiveKit trunk ID needed.
            </div>
            <Input label="SIP URI" value={data.sip_uri} onChange={e => setData({ ...data, sip_uri: e.target.value })} placeholder="sip:trunk.example.com or sips:trunk.example.com" helper="Required. sip: or sips: scheme; ;transport= optional" />
            <Input label="SIP username" value={data.auth_username} onChange={e => setData({ ...data, auth_username: e.target.value })} />
            <Input label="SIP password" type="password" value={data.auth_password} onChange={e => setData({ ...data, auth_password: e.target.value })} helper="Required. Encrypted via SettingsCrypto before storage" />
          </>
        )}

        <Input
          label="Caller-ID (E.164)"
          value={data.caller_id_e164}
          onChange={e => setData({ ...data, caller_id_e164: e.target.value })}
          placeholder="+14155551234"
          helper={isManaged ? "Must be a number on your Twilio account; Trust-Hub-verified for STIR/SHAKEN Tier A" : "Must be a number the BYO carrier allows as From"}
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label="Region (ISO)" value={data.region_iso} onChange={e => setData({ ...data, region_iso: e.target.value })} />
          <Input label="Max concurrent" type="number" value={data.max_concurrent_calls} onChange={e => setData({ ...data, max_concurrent_calls: Number(e.target.value) })} />
          <Input label="Max CPS" type="number" step="0.1" value={data.max_cps} onChange={e => setData({ ...data, max_cps: Number(e.target.value) })} />
          <Input label="Daily cap (USD)" type="number" step="0.01" value={data.daily_spend_cap_usd} onChange={e => setData({ ...data, daily_spend_cap_usd: Number(e.target.value) })} />
          <Input label="Rate per minute (USD)" type="number" step="0.001" value={data.rate_per_minute_usd} onChange={e => setData({ ...data, rate_per_minute_usd: Number(e.target.value) })} />
        </div>
        {error && <div style={{ color: COLORS.pink, fontSize: 13 }} data-testid="trunk-create-error">{error}</div>}
      </div>
    </Modal>
  );
}


function TestTrunkModal({ trunk, onClose }) {
  const [mode, setMode] = useState("dry");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [testPhone, setTestPhone] = useState(null);  // D10: tenant test number
  const [loadingPhone, setLoadingPhone] = useState(true);

  // D10 — fetch the tenant's configured test phone number when the
  // modal opens so the wet-mode option can validate it's both set
  // AND a valid E.164 string before the user can submit. Pre-fix
  // the wet-run would fail server-side with a generic 412 error.
  useEffect(() => {
    if (!trunk) return;
    setLoadingPhone(true);
    (async () => {
      try {
        const s = await outboundVoiceApi.getSettings();
        setTestPhone(s?.voice_outbound_test_phone_number ?? null);
      } catch (e) {
        // Settings fetch failure shouldn't prevent the modal from
        // rendering — just treat as "no test phone configured".
        setTestPhone(null);
      } finally {
        setLoadingPhone(false);
      }
    })();
  }, [trunk?.id]);

  if (!trunk) return null;

  const testPhoneValid = isValidE164Trunk(testPhone);
  const wetBlocked = mode === "wet" && !testPhoneValid;

  const run = async () => {
    if (wetBlocked) {
      setError(testPhone
        ? `Test phone "${testPhone}" is not a valid E.164 number (need +<country-code><number>).`
        : "Tenant test phone number not set. Configure voice_outbound_test_phone_number in Outbound Voice settings before running wet-mode."
      );
      return;
    }
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await outboundVoiceApi.testTrunk(trunk.id, mode === "wet");
      setResult(res);
    } catch (e) {
      setError(e.message);
    } finally { setRunning(false); }
  };

  return (
    <Modal open={!!trunk} onClose={onClose} title={`Test trunk: ${trunk.name}`} width={520}
      footer={<>
        <Button onClick={onClose} variant="ghost">Close</Button>
        <Button onClick={run} disabled={running || wetBlocked} title={wetBlocked ? "Configure a valid E.164 test phone before wet-run" : undefined}>
          {running ? "Testing…" : `Run ${mode} test`}
        </Button>
      </>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, marginBottom: 4 }}>Test mode</div>
          <select value={mode} onChange={e => setMode(e.target.value)} style={{ width: "100%", padding: 8, background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, borderRadius: 6 }}>
            <option value="dry">Dry-run (config check only, no call placed)</option>
            <option value="wet">Wet-run (places a real call to tenant's test_phone_number)</option>
          </select>
          {mode === "wet" && (
            <div style={{ marginTop: 6, fontSize: 12 }}>
              {loadingPhone ? (
                <span style={{ color: COLORS.textMuted }}>Loading test phone configuration…</span>
              ) : !testPhone ? (
                <span style={{ color: COLORS.pink }}>
                  ✗ <strong>voice_outbound_test_phone_number not set.</strong> Configure it in Outbound Voice settings before wet-running.
                </span>
              ) : !testPhoneValid ? (
                <span style={{ color: COLORS.pink }}>
                  ✗ Configured test phone <code>{testPhone}</code> is not a valid E.164 number (need <code>+CCNNN…</code>, 8-15 digits).
                </span>
              ) : (
                <span style={{ color: COLORS.orange }}>
                  ⚠ This places a real billable call to <code>{testPhone}</code>. Make sure it's a number you own.
                </span>
              )}
            </div>
          )}
        </div>
        {result && (
          <pre style={{ background: COLORS.bg, padding: 12, borderRadius: 6, fontSize: 12, overflow: "auto", maxHeight: 300 }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
        {error && <div style={{ color: COLORS.pink, fontSize: 13 }}>{error}</div>}
      </div>
    </Modal>
  );
}
